import React from 'react';
import '../styles/HeritageHeading.css';

/**
 * HeritageHeading
 * ----------------
 * Reusable foil-gold display heading that wraps the dimensional Cinzel
 * treatment used on the "SHOW UP READY" / "MOVE THINGS FORWARD" / "THE
 * WHOLE JOURNEY" waypoint titles on /pricing.
 *
 * Use this component anywhere you would otherwise re-copy the foil-gold
 * gradient + bevel CSS: À La Carte upsell hooks, future CADENCE Compass
 * health guides, new marketing pages, modal headers, etc.
 *
 * The dimensional CSS lives in HeritageHeading.css; this component is the
 * source of truth for which DOM tag + size class combination to render.
 *
 * @example
 *   <HeritageHeading>Show Up Ready</HeritageHeading>
 *
 * @example  Eyebrow above a section title
 *   <HeritageHeading as="span" size="eyebrow">À La Carte</HeritageHeading>
 *
 * @example  Subhead inside a card
 *   <HeritageHeading as="h3" size="subhead">Patient Scripts</HeritageHeading>
 */
export const HeritageHeading = ({
  // Semantic tag — use h1/h2/h3 for real headings, span for inline eyebrows.
  as: Tag = 'h2',
  // Size token — "display" (largest, waypoint scale), "subhead" (mid),
  // or "eyebrow" (smallest, all-caps wordmark style).
  size = 'display',
  // The single supported variant for now. Kept as a prop so we can add
  // more variants (e.g., "ivory", "copper-engraved") without breaking
  // the API.
  variant = 'foil-gold',
  // Optional extra class names from the caller for spacing / margin.
  className = '',
  // Defaults uppercase for eyebrows, lowercase otherwise. Caller can
  // override with the `uppercase` boolean prop.
  uppercase,
  children,
  'data-testid': dataTestId,
  ...rest
}) => {
  const isUppercase = uppercase ?? size === 'eyebrow';

  const classes = [
    'heritage-heading',
    `heritage-heading--${variant}`,
    `heritage-heading--${size}`,
    isUppercase ? 'heritage-heading--uppercase' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Tag className={classes} data-testid={dataTestId} {...rest}>
      {children}
    </Tag>
  );
};

export default HeritageHeading;
